import React from 'react';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
  maxVisiblePages?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  className = '',
  maxVisiblePages = 7,
}) => {
  const getVisiblePageNumbers = (): (number | string)[] => {
    if (totalPages <= maxVisiblePages) {
      // If total pages is less than max, show all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const sidePages = 2; // Number of pages to show on each side of current page

    // Always show first page
    pages.push(1);

    // Calculate start and end of the range around current page
    let start = Math.max(2, currentPage - sidePages);
    let end = Math.min(totalPages - 1, currentPage + sidePages);

    // Adjust if we're near the start
    if (currentPage <= sidePages + 1) {
      end = Math.min(totalPages - 1, maxVisiblePages - 2);
    }

    // Adjust if we're near the end
    if (currentPage >= totalPages - sidePages) {
      start = Math.max(2, totalPages - (maxVisiblePages - 2));
    }

    // Add ellipsis after first page if needed
    if (start > 2) {
      pages.push('ellipsis-start');
    }

    // Add pages in the range
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      pages.push('ellipsis-end');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);

  if (totalPages <= 1 && totalCount === 0) {
    return null;
  }

  return (
    <div className={`pagination-container ${className}`}>
      <div className="pagination-info">
        Showing {start} to {end} of {totalCount} entries
      </div>

      <div className="pagination-controls">
        <div className="pagination-page-numbers">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button pagination-button-nav"
          >
            Previous
          </button>

          {getVisiblePageNumbers().map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="pagination-ellipsis"
                >
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button pagination-button-page ${
                  pageNum === currentPage ? 'pagination-button-active' : ''
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button pagination-button-nav"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
